import { createPortal } from 'react-dom';
import { useAnchoredMenu } from '../useAnchoredMenu';
import { RailButton } from '../EditorTopBar/RailButton';
import { PdfIcon } from '../EditorTopBar/icons';

export type BriefPdfVariantChoice = 'client' | 'full';

const MENU_WIDTH = 274;

export const BRIEF_PDF_OPTIONS: Array<{
  variant: BriefPdfVariantChoice;
  label: string;
  gloss: string;
}> = [
  {
    variant: 'client',
    label: 'Client copy',
    gloss: 'Client fields only. What the client sees.',
  },
  {
    variant: 'full',
    label: 'Full brief (incl. internal)',
    gloss: 'Everything, including your notes and the transcript.',
  },
];

/**
 * The admin download control on an interview brief (mock-up §08). The client
 * copy comes first and takes focus when the menu opens, so the safe file is
 * the one a keyboard lands on: one deliberate move stands between Henry and
 * emailing his private notes to a client, which is the whole reason briefs
 * get a menu rather than the plain Download PDF icon ordinary scripts get.
 */
export function BriefPdfMenuButton({
  onDownload,
}: {
  onDownload: (variant: BriefPdfVariantChoice) => void;
}) {
  const menu = useAnchoredMenu({ width: MENU_WIDTH });

  function choose(variant: BriefPdfVariantChoice) {
    // The menu unmounts with the item that had focus; hand focus back to the
    // trigger so a keyboard user is not dropped at the top of the page.
    menu.close({ focusAnchor: true });
    onDownload(variant);
  }

  return (
    <>
      <RailButton
        ref={menu.anchorRef}
        icon={<PdfIcon />}
        label="Download PDF"
        hasMenu
        pressed={menu.open}
        expanded={menu.open}
        onClick={menu.toggle}
      />

      {menu.open &&
        menu.popupStyle &&
        createPortal(
          <div ref={menu.menuRef} role="menu" className="brief-menu" style={menu.popupStyle}>
            {BRIEF_PDF_OPTIONS.map((o) => (
              <button
                key={o.variant}
                type="button"
                role="menuitem"
                className="brief-menu__item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o.variant)}
              >
                <span className="brief-menu__body">
                  <span className="brief-menu__name">{o.label}</span>
                  <span className="brief-menu__gloss">{o.gloss}</span>
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
